import { CHANNEL, type Channel } from 'src/data/channel';
// The specific module, not the `src/data/generationTool` barrel: that barrel reaches back into this
// folder, and the cycle surfaces at runtime as an undefined import.
import { GENERATION_TOOL, type GenerationTool } from 'src/data/generationTool/const';
import { createEnumClampFunction } from 'src/lib/enum';

import type { Template } from './types';

export const TEMPLATE_BUILDER_PARAMS = {
  multiAsset: 'fields',
  csv: 'csv',
};

export const TEMPLATE_STATUS = {
  active: 'active',
  draft: 'draft',
  released: 'released',
} as const;
export type TemplateStatus = (typeof TEMPLATE_STATUS)[keyof typeof TEMPLATE_STATUS];

export const isAnywordMultiPromptTemplateStatus = (status: TemplateStatus): boolean =>
  status === TEMPLATE_STATUS.released;
export const isAnywordMultiPromptTemplate = (template: Template): boolean =>
  isAnywordMultiPromptTemplateStatus(template?.status);

/**
 * Template tag groups
 * @property {TEMPLATE_TAG_GROUP} DEFAULT - default tag group
 */
export const TEMPLATE_TAG_GROUP = {
  channel: 'channel',
  b2b: 'b2b',
  b2c: 'b2c',
} as const;
export type TemplateTagGroup = (typeof TEMPLATE_TAG_GROUP)[keyof typeof TEMPLATE_TAG_GROUP];

export const DEFAULT_TEMPLATE_TAG_GROUP = TEMPLATE_TAG_GROUP.channel;

export const clampTemplateTagGroup = createEnumClampFunction(
  TEMPLATE_TAG_GROUP,
  TEMPLATE_TAG_GROUP.channel
);

const TEMPLATE_TAG_GROUP_LABELS = {
  [TEMPLATE_TAG_GROUP.channel]: 'Channels',
  [TEMPLATE_TAG_GROUP.b2b]: 'B2B',
  [TEMPLATE_TAG_GROUP.b2c]: 'B2C',
};
export const getTemplateTagGroupLabel = (tagGroup: TemplateTagGroup): string =>
  TEMPLATE_TAG_GROUP_LABELS[tagGroup];

/**
 * Template tag
 * @enum {string}
 */
export const TEMPLATE_TAG = {
  // Channels (with `TEMPLATE_TAG_GROUP.channel` prefix)
  channelSocial: 'channel_social',
  channelAds: 'channel_ads',
  channelEmail: 'channel_email',
  channelWebsite: 'channel_website',
  channelBlog: 'channel_blog',
  channelEcommerce: 'channel_ecommerce',
  channelImages: 'channel_images',
  channelOther: 'channel_other',
  // B2B (with `TEMPLATE_TAG_GROUP.b2b` prefix)
  b2bDemandGeneration: 'b2b_demand_generation',
  b2bProductMarketing: 'b2b_product_marketing',
  b2bContentMarketing: 'b2b_content_marketing',
  b2bAbm: 'b2b_abm',
  b2bFieldMarketing: 'b2b_field_marketing',
  b2bBrandCommunications: 'b2b_brand_communications',
  // B2C (with `TEMPLATE_TAG_GROUP.b2c` prefix)
  b2cPerformance: 'b2c_performance',
  b2cBrandCreative: 'b2c_brand_creative',
  b2cContentSocial: 'b2c_content_social',
  b2cLifecycleRetention: 'b2c_lifecycle_retention',
  b2cProductMarketing: 'b2c_product_marketing',
  b2cPrCommunications: 'b2c_pr_communications',
  b2cEcommerceWebsiteOptimization: 'b2c_ecommerce_website_optimization',
} as const;
export type TemplateTag = (typeof TEMPLATE_TAG)[keyof typeof TEMPLATE_TAG];

const TEMPLATE_TAG_LABELS = {
  // Channels
  [TEMPLATE_TAG.channelSocial]: 'Social',
  [TEMPLATE_TAG.channelAds]: 'Ads',
  [TEMPLATE_TAG.channelEmail]: 'Email',
  [TEMPLATE_TAG.channelWebsite]: 'Website',
  [TEMPLATE_TAG.channelBlog]: 'Blog / Article',
  [TEMPLATE_TAG.channelEcommerce]: 'Ecommerce',
  [TEMPLATE_TAG.channelImages]: 'Images',
  [TEMPLATE_TAG.channelOther]: 'Other',
  // B2B
  [TEMPLATE_TAG.b2bDemandGeneration]: 'Demand generation',
  [TEMPLATE_TAG.b2bProductMarketing]: 'Product marketing',
  [TEMPLATE_TAG.b2bContentMarketing]: 'Content marketing and SEO',
  [TEMPLATE_TAG.b2bAbm]: 'ABM',
  [TEMPLATE_TAG.b2bFieldMarketing]: 'Field/event marketing',
  [TEMPLATE_TAG.b2bBrandCommunications]: 'Brand & communications',
  // B2C
  [TEMPLATE_TAG.b2cPerformance]: 'Performance',
  [TEMPLATE_TAG.b2cBrandCreative]: 'Brand & creative',
  [TEMPLATE_TAG.b2cContentSocial]: 'Content & social',
  [TEMPLATE_TAG.b2cLifecycleRetention]: 'Lifecycle & retention',
  [TEMPLATE_TAG.b2cProductMarketing]: 'Product marketing',
  [TEMPLATE_TAG.b2cPrCommunications]: 'PR & communications',
  [TEMPLATE_TAG.b2cEcommerceWebsiteOptimization]: 'Ecommerce & website optimization',
};
export const getTemplateTagLabel = (tag: TemplateTag): string => TEMPLATE_TAG_LABELS[tag];

/**
 * Get template tags by tag group
 * @param tagGroup - tag group
 * @returns - template tags
 */
export const getTemplateTagsByGroup = (tagGroup: TemplateTagGroup): TemplateTag[] => {
  const tagGroupPrefix = `${tagGroup}_`;
  return Object.values(TEMPLATE_TAG).filter((tag) => tag.startsWith(tagGroupPrefix));
};

/**
 * Get template tag group by tag
 * @param tag - template tag
 * @returns - template tag group
 */
export const getTemplateTagGroupByTag = (tag: TemplateTag): TemplateTagGroup => {
  const tagPrefix = tag.split('_')[0];
  return clampTemplateTagGroup(tagPrefix);
};

/**
 * Check if tag is a "channel" tag
 * @param tag - template tag
 * @returns - is channel tag
 */
export const isChannelTemplateTag = (tag: TemplateTag): boolean =>
  getTemplateTagGroupByTag(tag) === TEMPLATE_TAG_GROUP.channel;

/**
 * Get channel tag from the list of template tags
 * @param tags - template tags
 * @returns - channel tag
 */
export const getChannelTagFromTemplateTags = (tags: TemplateTag[]): TemplateTag => {
  const channelTags = tags.filter(isChannelTemplateTag);
  return channelTags.length > 0 ? channelTags[0] : TEMPLATE_TAG.channelOther;
};

export const getTagLabelValues = (): string[] => {
  return Object.values(TEMPLATE_TAG_LABELS);
};

/**
 * One branch of a tag that holds too many templates to browse as a single list. A template reaches
 * its branch through the channel its own tool declares, so a newly added tool needs no edit here.
 * Only the order and the wording are declared — a channel labels itself "Facebook" and "Twitter"
 * where the ad platforms are named "Meta" and "X".
 */
export interface TemplateTagChannelBranch {
  label: string;
  channels: Channel[];
  /** The tools the registry leaves without a channel of their own, which nothing else can place. */
  tools?: GenerationTool[];
}

/** The branch for whatever the others do not claim. Never empty in the tree, often empty in fact. */
const OTHER_CHANNEL_BRANCH: TemplateTagChannelBranch = { label: 'Other', channels: [] };

const TEMPLATE_TAG_CHANNEL_BRANCHES: Partial<Record<TemplateTag, TemplateTagChannelBranch[]>> = {
  [TEMPLATE_TAG.channelSocial]: [
    {
      label: 'Instagram',
      channels: [CHANNEL.instagram],
    },
    {
      label: 'Facebook',
      channels: [CHANNEL.facebook, CHANNEL.facebookPages],
    },
    {
      label: 'LinkedIn',
      channels: [CHANNEL.linkedin, CHANNEL.linkedinSocial],
    },
    {
      label: 'X',
      channels: [CHANNEL.twitter, CHANNEL.twitterSocial],
    },
    {
      label: 'Pinterest',
      channels: [CHANNEL.pinterest],
      tools: [GENERATION_TOOL.pinterestMixturePost],
    },
    {
      label: 'YouTube',
      channels: [CHANNEL.youtube],
      tools: [GENERATION_TOOL.youtubeMixturePost],
    },
    OTHER_CHANNEL_BRANCH,
  ],
  [TEMPLATE_TAG.channelAds]: [
    {
      label: 'Google Ads',
      channels: [CHANNEL.google],
    },
    {
      label: 'LinkedIn Ads',
      channels: [CHANNEL.linkedin],
    },
    {
      label: 'Meta Ads',
      channels: [CHANNEL.facebook],
    },
    {
      label: 'Outbrain Ads',
      channels: [CHANNEL.outbrain],
    },
    {
      label: 'Pinterest Ads',
      channels: [CHANNEL.pinterest],
      tools: [GENERATION_TOOL.pinterestMixtureAd],
    },
    {
      label: 'Taboola Ads',
      channels: [CHANNEL.taboola],
    },
    {
      label: 'X Ads',
      channels: [CHANNEL.twitter],
      tools: [GENERATION_TOOL.twitterMixtureAd],
    },
    OTHER_CHANNEL_BRANCH,
  ],
};

export const getTemplateTagChannelBranches = (tag: TemplateTag): TemplateTagChannelBranch[] =>
  TEMPLATE_TAG_CHANNEL_BRANCHES[tag] || [];

/**
 * A tool with the channel the registry gives it. The channel is resolved by the caller: reading it
 * here would close the cycle this module is careful to keep open.
 */
export interface TemplateChannelTool {
  generationTool: GenerationTool;
  channel?: Channel;
}

interface TemplateTagChannelBranchMatch {
  branch: TemplateTagChannelBranch;
  /** Every branch of the same tag, so the catch-all knows what the others already claimed. */
  branches: TemplateTagChannelBranch[];
  /** Every tool the template writes with — a campaign covers as many platforms as it has blocks. */
  templateTools: TemplateChannelTool[];
}

/** Whether a template belongs in `branch`. A template writing for several platforms belongs in each. */
export const isTemplateTagChannelBranchMatch = ({
  branch,
  branches,
  templateTools,
}: TemplateTagChannelBranchMatch): boolean => {
  const claims = (candidate: TemplateTagChannelBranch) =>
    templateTools.some(
      ({ generationTool, channel }) =>
        Boolean(channel && candidate.channels.includes(channel)) ||
        Boolean(candidate.tools?.includes(generationTool))
    );

  if (branch.channels.length || branch.tools?.length) {
    return claims(branch);
  }

  return !branches.some((other) => other !== branch && claims(other));
};
